import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import Link from '../../Link';
import InfoTooltip from '../shared/InfoTooltip';
import { StatusPill, TypePills, STATUS_LABELS } from './EarningsTable';
import { formatMoney, formatDay, photoUrl, DASH } from '../shared/reportingFormat';
import messages from '../../../locale/jsonMessages';
import sh from '../shared/OwnerReporting.css';
import s from './OwnerEarnings.css';

export const TRANSACTIONS_PATH = '/user/transaction/completed';

// Detail of one reservation line (side panel): breakdown by component, commission, status, references. Every amount is the one
// returned by the server for the line.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// `opener` = the element that opened the panel: it gets the focus back on close.
const EarningDetail = ({ row, currency, onClose, opener }) => {
  const { formatMessage, locale } = useIntl();
  const closeRef = useRef(null);
  const panelRef = useRef(null);
  const styles = { ...sh, ...s };

  useEffect(() => {
    if (closeRef.current) closeRef.current.focus();
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The page behind does not scroll while the panel is open; the focus goes back to the button that opened it.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  // Keyboard focus stays inside the panel (Tab / Shift+Tab loop), as announced by aria-modal.
  const trapFocus = (event) => {
    if (event.key !== 'Tab' || !panelRef.current) return;
    const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  if (!row) return null;
  const components = row.components || {};
  const payment = row.payment;
  const photo = photoUrl(row.photo);
  const money = (value) => formatMoney(value, currency, locale);
  const line = (label, value, { help, strong } = {}) => (
    <li className={`${s.detailLine} ${strong ? s.detailTotal : ''}`}>
      <span className={s.detailLabel}>{label}{help && <InfoTooltip text={help} />}</span>
      <span className={s.detailValue}>{value}</span>
    </li>
  );

  return (
    <div className={s.drawerRoot}>
      <div className={s.drawerBackdrop} onClick={onClose} aria-hidden="true" />
      <aside ref={panelRef} onKeyDown={trapFocus} className={s.drawer} role="dialog" aria-modal="true" aria-label={formatMessage(messages.orpDetailTitle, { code: row.confirmationCode })}>
        <div className={s.drawerHead}>
          <div>
            <h2 className={s.drawerTitle}>{formatMessage(messages.orpDetailTitle, { code: row.confirmationCode })}</h2>
            <StatusPill row={row} styles={styles} />
          </div>
          <button type="button" ref={closeRef} className={s.drawerClose} onClick={onClose} aria-label={formatMessage(messages.orpDetailClose)}>&times;</button>
        </div>
        <div className={s.drawerBody}>
          <div className={s.detailVehicle}>
            <span className={s.thumb} style={photo ? { backgroundImage: `url("${photo}")` } : undefined} aria-hidden="true" />
            <span className={s.vehicleTitle}>{row.title}</span>
          </div>
          <div className={s.detailSection}>
            <ul className={s.detailList}>
              {line(formatMessage(messages.orpDetailDates), `${formatDay(row.checkIn, locale)} → ${formatDay(row.checkOut, locale)}`)}
              {line(formatMessage(messages.orpDetailGuest), row.guestName || DASH)}
              {line(formatMessage(messages.orpColTypes), <TypePills components={components} styles={styles} />)}
            </ul>
          </div>
          <div className={s.detailSection}>
            <h3 className={s.detailHeading}>{formatMessage(messages.orpDetailBreakdown)}</h3>
            <ul className={s.detailList}>
              {line(formatMessage(messages.orpBreakdownRental), money(components.rental))}
              {line(formatMessage(messages.orpBreakdownOptions), money(components.options))}
              {line(formatMessage(messages.orpBreakdownDelivery), money(components.delivery))}
              {Number(components.other) !== 0 && components.other !== undefined && components.other !== null && line(formatMessage(messages.orpBreakdownOther), money(components.other))}
              {line(formatMessage(messages.orpDetailGross), money(row.grossAmount), { strong: true })}
              {line(formatMessage(messages.orpDetailCommission), money(row.commission), { help: formatMessage(messages.orpHelpCommission) })}
              {Number(row.promoAdjustment) !== 0 && row.promoAdjustment !== undefined && row.promoAdjustment !== null
                && line(formatMessage(messages.orpDetailPromo), money(row.promoAdjustment), { help: formatMessage(messages.orpHelpPromo) })}
              {line(formatMessage(messages.orpDetailNet), money(row.netRevenue), { strong: true })}
            </ul>
          </div>
          <div className={s.detailSection}>
            <h3 className={s.detailHeading}>{formatMessage(messages.orpDetailPayment)}</h3>
            <ul className={s.detailList}>
              {line(formatMessage(messages.orpColStatus), STATUS_LABELS[row.revenueStatus] ? formatMessage(messages[STATUS_LABELS[row.revenueStatus]]) : DASH)}
              {payment
                ? (
                  <>
                    {line(formatMessage(messages.orpColMethod), payment.method || DASH)}
                    {line(formatMessage(messages.orpColReference), payment.reference || DASH)}
                    {payment.providerStatus && line(formatMessage(messages.orpDetailProviderStatus), payment.providerStatus)}
                    {line(formatMessage(messages.orpDetailPaidAmount), money(payment.paidAmount))}
                  </>
                )
                : null}
            </ul>
            {!payment && <p className={s.detailMuted}>{formatMessage(messages.orpDetailNoPayment)}</p>}
            <Link to={TRANSACTIONS_PATH} className={s.drawerLink}>{formatMessage(messages.orpDetailSeeTransactions)}</Link>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default withStyles(sh, s)(EarningDetail);
